// ANN-1/2/3/4/6: text, label, dimension, and grid-entity rendering (08-annotation.md).
// Geometry/formatting math lives in doc/derived.ts (shared with io/ exporters).
import { angleOf, dir, dist, sub, type Vec } from "../core/geometry";
import { dimensionGeometry, dimensionText, indexLabel } from "../doc/derived";
import { BASE_PPMM } from "./camera";
import type {
  Dash,
  DimensionEntity,
  Doc,
  GridEntity,
  LabelEntity,
  Layer,
  TextEntity,
  TextStyle
} from "../doc/entities";
import type { RenderView } from "./renderEntities";

export { dimensionGeometry, dimensionText, indexLabel, type DimGeometry } from "../doc/derived";

const FONT_STACKS: Record<TextStyle["font"], string> = {
  Inter: "Inter, system-ui, sans-serif",
  Caveat: "Caveat, cursive",
  "JetBrains Mono": "'JetBrains Mono', Consolas, monospace"
};

export function fontString(ts: TextStyle): string {
  return `${ts.bold ? 700 : 400} ${ts.size}px ${FONT_STACKS[ts.font]}`;
}

const LINE_HEIGHT = 1.3; // ANN-1

export function renderText(v: RenderView, e: TextEntity, layer: Layer | undefined): void {
  const { ctx } = v;
  ctx.save();
  ctx.translate(e.pos.x, e.pos.y);
  if (Math.abs(e.rotationDeg) > 1e-9) ctx.rotate((e.rotationDeg * Math.PI) / 180);
  ctx.font = fontString(e.textStyle);
  ctx.fillStyle = e.textStyle.color;
  ctx.globalAlpha = e.textStyle.opacity * (layer?.opacity ?? 1);
  ctx.textAlign = e.textStyle.align;
  ctx.textBaseline = "top";
  const lineH = e.textStyle.size * LINE_HEIGHT;
  e.content.split("\n").forEach((line, i) => ctx.fillText(line, 0, i * lineH));
  ctx.restore();
}

export function renderLabel(v: RenderView, e: LabelEntity, layer: Layer | undefined): void {
  const { ctx } = v;
  const alpha = layer?.opacity ?? 1;
  ctx.save();
  ctx.globalAlpha = e.style.opacity * alpha;
  ctx.strokeStyle = e.style.strokeColor;
  ctx.lineWidth = Math.max(
    e.style.widthMode === "world" ? e.style.strokeWidth : e.style.strokeWidth / v.cam.scale,
    1 / (v.dpr * v.cam.scale)
  );
  ctx.beginPath();
  ctx.moveTo(e.head.x, e.head.y);
  ctx.lineTo(e.tail.x, e.tail.y);
  ctx.stroke();

  const toward = dist(e.head, e.tail) > 1e-9 ? dir(angleOf(sub(e.head, e.tail))) : { x: 1, y: 0 };
  if (e.arrow === "arrow") {
    drawArrowhead(ctx, e.head, toward, 3, e.style.strokeColor);
  } else if (e.arrow === "dot") {
    ctx.fillStyle = e.style.strokeColor;
    ctx.beginPath();
    ctx.arc(e.head.x, e.head.y, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }

  // text at the tail, side by tail-relative-to-head x-sign (EC-ANN-2: vertical → right)
  const right = e.tail.x >= e.head.x;
  ctx.font = fontString(e.textStyle);
  ctx.fillStyle = e.textStyle.color;
  ctx.globalAlpha = e.textStyle.opacity * alpha;
  ctx.textAlign = right ? "left" : "right";
  ctx.textBaseline = "middle";
  ctx.fillText(e.content, e.tail.x + (right ? 1 : -1), e.tail.y);
  ctx.restore();
}

function drawArrowhead(
  ctx: CanvasRenderingContext2D,
  tip: Vec,
  toward: Vec,
  sizeMm: number,
  color: string
): void {
  const back = { x: -toward.x, y: -toward.y };
  const n = { x: -back.y, y: back.x };
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(tip.x + back.x * sizeMm + n.x * sizeMm * 0.35, tip.y + back.y * sizeMm + n.y * sizeMm * 0.35);
  ctx.lineTo(tip.x + back.x * sizeMm - n.x * sizeMm * 0.35, tip.y + back.y * sizeMm - n.y * sizeMm * 0.35);
  ctx.closePath();
  ctx.fill();
}

// ---- dimensions (ANN-3/4/5) — geometry/formatting imported from doc/derived ----------

export function renderDimension(v: RenderView, e: DimensionEntity, doc: Doc, layer: Layer | undefined): void {
  const g = dimensionGeometry(doc, e);
  if (!g) return;
  const { ctx } = v;
  const ds = doc.dimStyle;
  // ANN-5 amendment: dim styling is PAPER/SCREEN-sized — constant px on screen, dimStyle mm on paper
  const pm = v.dimPm ?? BASE_PPMM / v.cam.scale;
  // line weight follows the same rule: constant on screen, in step with the text in an export
  const lineW = v.dimPm ? (ds.strokeWidthPx * v.dimPm) / BASE_PPMM : ds.strokeWidthPx / v.cam.scale;
  const orphan = e.meta?.orphan === true;
  const color = orphan ? "#e05c5c" : ds.color; // INV-6 orphan tint
  const alpha = layer?.opacity ?? 1;
  ctx.save();
  ctx.globalAlpha = (orphan ? 0.6 : 1) * alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineW;

  const u = dist(g.pb, g.pa) > 1e-9 ? dir(angleOf(sub(g.pb, g.pa))) : { x: 1, y: 0 };
  if (e.kind === "linear") {
    // extension lines: from near the anchor (gap) to past the dim line (overshoot)
    for (const [from, to] of [
      [g.a, g.pa],
      [g.b, g.pb]
    ] as const) {
      const len = dist(from, to);
      if (len > 1e-9) {
        const eu = dir(angleOf(sub(to, from)));
        ctx.beginPath();
        ctx.moveTo(from.x + eu.x * ds.extGapMm * pm, from.y + eu.y * ds.extGapMm * pm);
        ctx.lineTo(to.x + eu.x * ds.extOvershootMm * pm, to.y + eu.y * ds.extOvershootMm * pm);
        ctx.stroke();
      }
    }
  }
  // dimension / leader line
  ctx.beginPath();
  ctx.moveTo(g.pa.x, g.pa.y);
  ctx.lineTo(g.pb.x, g.pb.y);
  ctx.stroke();
  if (e.kind === "linear") {
    drawArrowhead(ctx, g.pa, { x: -u.x, y: -u.y }, ds.arrowMm * pm, color);
    drawArrowhead(ctx, g.pb, u, ds.arrowMm * pm, color);
  } else {
    drawArrowhead(ctx, g.pa, { x: -u.x, y: -u.y }, ds.arrowMm * pm, color); // arrow at the arc edge
  }

  // value text, centered on the line with a small offset
  const text = dimensionText(doc, e, g.value);
  ctx.font = fontString({ ...ds.textStyle, size: ds.textStyle.size * pm });
  ctx.fillStyle = orphan ? "#e05c5c" : ds.textStyle.color;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.translate(g.textPos.x, g.textPos.y);
  if (e.kind === "linear" && Math.abs(g.textAngleDeg) > 1e-9) {
    ctx.rotate((g.textAngleDeg * Math.PI) / 180);
  }
  ctx.fillText(text, 0, -lineW - 0.5 * pm);
  ctx.restore();
}

// ---- grid entity (ANN-6) ------------------------------------------------------------

function gridDash(dash: Dash, w: number): number[] {
  switch (dash) {
    case "dashed":
      return [4 * w, 2 * w];
    case "dotted":
      return [0.5 * w, 2 * w];
    case "dashdot":
      return [6 * w, 2 * w, 0.5 * w, 2 * w];
    default:
      return [];
  }
}

export function renderGrid(v: RenderView, e: GridEntity, layer: Layer | undefined): void {
  const { ctx } = v;
  const w = e.cols * e.cellW;
  const h = e.rows * e.cellH;
  ctx.save();
  ctx.globalAlpha = layer?.opacity ?? 1;
  ctx.translate(e.pos.x, e.pos.y);
  if (Math.abs(e.rotationDeg) > 1e-9) ctx.rotate((e.rotationDeg * Math.PI) / 180);

  const drawLines = (
    count: number,
    style: { color: string; width: number; dash: Dash },
    vertical: boolean
  ): void => {
    ctx.strokeStyle = style.color;
    for (let i = 0; i <= count; i++) {
      const major = e.majorEvery > 0 && i % e.majorEvery === 0;
      ctx.lineWidth = Math.max(style.width * (major ? 2 : 1), 1 / (v.dpr * v.cam.scale));
      ctx.setLineDash(gridDash(style.dash, style.width));
      ctx.beginPath();
      if (vertical) {
        ctx.moveTo(i * e.cellW, 0);
        ctx.lineTo(i * e.cellW, h);
      } else {
        ctx.moveTo(0, i * e.cellH);
        ctx.lineTo(w, i * e.cellH);
      }
      ctx.stroke();
    }
    ctx.setLineDash([]);
  };
  drawLines(e.cols, e.vStyle, true);
  drawLines(e.rows, e.hStyle, false);

  // labels: columns above, rows left, half a cell outside (ANN-6)
  ctx.font = fontString(e.labels.textStyle);
  ctx.fillStyle = e.labels.textStyle.color;
  ctx.globalAlpha = e.labels.textStyle.opacity * (layer?.opacity ?? 1);
  if (e.labels.cols) {
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    for (let i = 0; i < e.cols; i++) {
      ctx.fillText(indexLabel(i, e.labels.colMode), (i + 0.5) * e.cellW, -e.cellH * 0.25);
    }
  }
  if (e.labels.rows) {
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let i = 0; i < e.rows; i++) {
      ctx.fillText(indexLabel(i, e.labels.rowMode), -e.cellW * 0.25, (i + 0.5) * e.cellH);
    }
  }
  ctx.restore();
}
